import { useState } from "react";
import { Plus, Film, FileText, Image as ImageIcon, Download, Trash2, Lock, Users, ListChecks, BarChart3, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { web, errMsg, playFileUrl } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, Btn, PillTabs, shortDate } from "../../components/app/kit";
import { Modal } from "../../components/app/Form";
import { PlayUpload, VISIBILITY } from "../../components/app/PlayUpload";
import { QuizEditor } from "../../components/app/QuizEditor";
import { QuizTake, QuizResults } from "../../components/app/QuizTake";
import { LinkPlayer, PlayLinks, embedOf } from "../../components/app/PlayLinks";

const ytThumb = (url) => { const e = embedOf(url ?? ""); const id = e?.includes("youtube") && e.split("/embed/")[1]; return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null; };

const quizLabel = (q, role) => {
  if (!q) return null;
  if (role === "coach") return `Quiz · ${q.passed ?? 0} passed`;
  return q.best == null ? "Quiz · not taken" : `Quiz · ${q.passed ? "passed" : "best"} ${q.best}%`;
};

const QuizBadge = ({ q, role }) => (q ? (
  <span data-testid="play-quiz-badge" className={`absolute right-3 top-3 flex items-center gap-1 rounded-full px-2.5 py-1 font-mono-gd text-[10px] font-bold uppercase tracking-wider ${q.passed && role !== "coach" ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-[#0A0E1A]/85 text-[#39FF14]"}`}>
    <ListChecks className="h-3 w-3" />{quizLabel(q, role)}
  </span>
) : null);

const ICON = { video: Film, pdf: FileText, image: ImageIcon, file: FileText, link: Link2 };
const visLabel = (v) => VISIBILITY.find(([k]) => k === v)?.[1] ?? "Whole team";

const PlayCard = ({ p, tid, role, onOpen }) => {
  const Icon = ICON[p.kind] ?? FileText;
  return (
    <li>
      <button data-testid={`play-card-${p.id}`} onClick={() => onOpen(p)}
        className="group block w-full overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0F172A] text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#39FF14]/60">
        <div className="relative flex aspect-video items-center justify-center bg-[#162032]">
          {p.kind === "image" ? <img src={playFileUrl(tid, p)} alt={p.title} className="h-full w-full object-cover" loading="lazy" />
            : p.kind === "link" && ytThumb(p.link_url) ? <img src={ytThumb(p.link_url)} alt={p.title} className="h-full w-full object-cover" loading="lazy" />
            : <Icon className="h-10 w-10 text-slate-400 transition-colors group-hover:text-[#39FF14]" />}
          <span className="absolute left-3 top-3 rounded-full bg-[#0A0E1A]/85 px-2.5 py-1 font-mono-gd text-[10px] uppercase tracking-wider text-slate-200">{p.kind === "link" ? "video link" : p.kind}</span>
          {p.links?.length > 0 && <span data-testid={`play-links-count-${p.id}`} className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-[#0A0E1A]/85 px-2.5 py-1 font-mono-gd text-[10px] uppercase tracking-wider text-[#39FF14]"><Link2 className="h-3 w-3" />{p.links.length} link{p.links.length === 1 ? "" : "s"}</span>}
          <QuizBadge q={p.quiz} role={role} />
        </div>
        <div className="px-4 py-3">
          <p className="truncate font-display text-xl font-bold uppercase text-white">{p.title}</p>
          <p className="mt-0.5 flex items-center gap-1.5 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
            {p.visibility === "coaches" ? <Lock className="h-3 w-3" /> : <Users className="h-3 w-3" />}{visLabel(p.visibility)} · {shortDate(p.created_at)}
          </p>
        </div>
      </button>
    </li>
  );
};

const QuizButtons = ({ play, role, onQuiz }) => {
  if (role === "coach") return (
    <>
      <Btn variant="ghost" onClick={() => onQuiz("edit")} data-testid="play-quiz-edit"><ListChecks className="h-4 w-4" />{play.quiz ? "Edit quiz" : "Add quiz"}</Btn>
      {play.quiz && <Btn variant="ghost" onClick={() => onQuiz("results")} data-testid="play-quiz-results"><BarChart3 className="h-4 w-4" />Results</Btn>}
    </>
  );
  if (role === "player" && play.quiz) return <Btn variant="ghost" onClick={() => onQuiz("take")} data-testid="play-quiz-take"><ListChecks className="h-4 w-4" />{play.quiz.best == null ? "Take quiz" : "Retake quiz"}</Btn>;
  return null;
};

const Viewer = ({ play, tid, canEdit, role, onQuiz, onClose, onDeleted, onChanged }) => {
  const url = play && (play.kind === "link" ? play.link_url : playFileUrl(tid, play));
  const remove = async () => {
    if (!window.confirm(`Delete "${play.title}"? It's removed from the GAMEDAY app too.`)) return;
    try {
      await web.delete(`/teams/${tid}/plays/${play.id}`);
      toast.success("Play deleted");
      onDeleted();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Modal open={!!play} onClose={onClose} testid="play-viewer" title={play?.title ?? ""} description={play ? `${play.category} · ${visLabel(play.visibility)}` : ""}>
      {play && (
        <div className="space-y-4">
          <div className="overflow-hidden rounded-xl bg-black">
            {play.kind === "link" ? <LinkPlayer url={url} title={play.title} />
              : play.kind === "video" ? <video data-testid="play-video" src={url} controls playsInline className="max-h-[60vh] w-full" />
              : play.kind === "image" ? <img data-testid="play-image" src={url} alt={play.title} className="max-h-[60vh] w-full object-contain" />
              : play.kind === "pdf" ? <iframe data-testid="play-pdf" src={url} title={play.title} className="h-[60vh] w-full bg-white" />
              : <p className="p-6 text-sm text-slate-300">Preview not available. Download the file to view it.</p>}
          </div>
          <PlayLinks tid={tid} play={play} canEdit={canEdit} onChanged={onChanged} />
          <div className="flex flex-wrap justify-end gap-2">
            <QuizButtons play={play} role={role} onQuiz={onQuiz} />
            {canEdit && <Btn variant="danger" onClick={remove} data-testid="play-delete"><Trash2 className="h-4 w-4" />Delete</Btn>}
            <a href={url} {...(play.kind === "link" ? {} : { download: play.filename ?? play.title })} target="_blank" rel="noreferrer" data-testid="play-download"
              className="inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-5 py-2.5 text-sm font-semibold text-[#0A0E1A] hover:bg-[#30D911]">{play.kind === "link" ? <><ExternalLink className="h-4 w-4" />Open link</> : <><Download className="h-4 w-4" />Download</>}</a>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default function Playbook() {
  const { membership, canEdit } = useAuth();
  const tid = membership?.team_id;
  const { data, loading, error, reload } = useApi(tid && `/teams/${tid}/plays`, web);
  const [tab, setTab] = useState("Offense");
  const [openId, setOpenId] = useState(null);
  const [upload, setUpload] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const role = data?.role ?? (canEdit ? "coach" : "player");
  const all = Array.isArray(data?.plays) ? data.plays : [];
  const open = all.find((p) => p.id === openId) ?? null;
  const setOpen = (p) => setOpenId(p?.id ?? null);
  const list = all.filter((p) => (p.category ?? "offense") === tab.toLowerCase());
  return (
    <div>
      <PageHeader eyebrow={membership?.team_name} title="Playbook">
        {canEdit && <Btn data-testid="add-play-button" onClick={() => setUpload(true)}><Plus className="h-4 w-4" />Post a play</Btn>}
      </PageHeader>
      <PillTabs tabs={["Offense", "Defense"]} value={tab} onChange={setTab} />
      {loading && !data ? <Loader /> : error ? <ErrorNote error={error} /> : !list.length ? (
        <Empty title={`No ${tab.toLowerCase()} plays`} text={canEdit ? "Post a diagram, a video or a video link so your team can study it." : "Your coach hasn't posted any yet."} />
      ) : (
        <ul data-testid="play-grid" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((p) => <PlayCard key={p.id} p={p} tid={tid} role={role} onOpen={setOpen} />)}
        </ul>
      )}
      <Viewer play={open} tid={tid} canEdit={canEdit} role={role} onQuiz={(mode) => { setQuiz({ play: open, mode }); setOpen(null); }} onClose={() => setOpen(null)} onDeleted={reload} onChanged={reload} />
      {canEdit && <QuizEditor open={quiz?.mode === "edit"} onClose={() => setQuiz(null)} tid={tid} play={quiz?.play} onSaved={reload} />}
      {canEdit && <QuizResults open={quiz?.mode === "results"} onClose={() => setQuiz(null)} tid={tid} play={quiz?.play} />}
      {role === "player" && <QuizTake open={quiz?.mode === "take"} onClose={() => setQuiz(null)} tid={tid} play={quiz?.play} onDone={reload} />}
      {canEdit && <PlayUpload open={upload} onClose={() => setUpload(false)} tid={tid} category={tab.toLowerCase()} onDone={reload} />}
    </div>
  );
}
